import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { formatDate, formatCurrency } from '../utils/numberFormatter';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/bitacora.css';
import '../styles/requests.css';
import '../styles/requestStatusBadges.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import RequestDetailModal from '../components/Requests/RequestDetailModal';
import BackHomeButton from '../components/Common/BackHomeButton';
import { Button, FormControl, TableCard } from '../components/ui';
import { REQUEST_STATUS_CONFIG } from '../config/requestStatusConfig';
import { resolveRequestPreset } from '../config/requestPresets';
import { trackPosSessionOpened } from '../analytics/events';
import { useAnalyticsContext } from '../analytics/useAnalyticsContext';

const MySwal = withReactContent(Swal);
const DEFAULT_PAGINATION = {
  current_page: 1,
  per_page: 25,
  total: 0,
  last_page: 1
};

// Función para obtener la configuración del estado
const getStatusConfig = (statusValue) => {
  return REQUEST_STATUS_CONFIG[statusValue] || {
    class: 'request-status-default',
    label: statusValue || 'Desconocido'
  };
};

const formatDateFilterLabel = (value) => {
  if (!value) return '';

  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const normalizePagination = (pagination, fallback = DEFAULT_PAGINATION) => {
  if (!pagination || typeof pagination !== 'object') {
    return { ...fallback };
  }

  return {
    current_page: Number(pagination.current_page || fallback.current_page || 1),
    per_page: Number(pagination.per_page || fallback.per_page || 25),
    total: Number(pagination.total || 0),
    last_page: Math.max(1, Number(pagination.last_page || fallback.last_page || 1))
  };
};

const RequestsPage = ({ forcedPresetKey = null }) => {
  const navigate = useNavigate();
  const { presetKey: routePresetKey } = useParams();
  const { commerceId, branchId, channel } = useAnalyticsContext();
  const activePreset = useMemo(
    () => resolveRequestPreset(forcedPresetKey || routePresetKey),
    [forcedPresetKey, routePresetKey]
  );
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    from_date: '',
    to_date: ''
  });
  const [searchInput, setSearchInput] = useState('');

  const hasActiveFilters = Boolean(
    searchInput.trim() || filters.status || filters.from_date || filters.to_date
  );

  const filterSummary = [
    filters.status ? getStatusConfig(filters.status).label : 'Todos los estados',
    filters.from_date ? `Desde ${formatDateFilterLabel(filters.from_date)}` : null,
    filters.to_date ? `Hasta ${formatDateFilterLabel(filters.to_date)}` : null,
    searchInput.trim() ? `"${searchInput.trim()}"` : null
  ]
    .filter(Boolean)
    .join(' · ');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      // Backend exige min 3 caracteres en search: solo enviar si 0 o >= 3
      const searchValue = (filters.search.length === 0 || filters.search.length >= 3)
        ? filters.search
        : '';
      const params = new URLSearchParams({
        ...filters,
        search: searchValue,
        page: pagination.current_page,
        per_page: pagination.per_page
      });

      const response = await api.get(`/requests?${params}`);
      setRequests(response.data.requests || []);
      setPagination((prev) => normalizePagination(response.data.pagination, prev));
    } catch (error) {
      console.error('Error al obtener solicitudes:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron cargar las solicitudes',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.current_page, pagination.per_page]);

  // Track POS session opened when entering requests module
  useEffect(() => {
    if (commerceId) {
      trackPosSessionOpened({
        commerceId,
        branchId,
        channel
      });
    }
  }, [commerceId, branchId, channel]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Solo enviar búsqueda a la API con 0 o >= 3 caracteres (backend exige min:3)
  useEffect(() => {
    const initialSearch = searchInput.length === 0 && !filters.search;
    if (!initialSearch && (searchInput.length === 0 || searchInput.length >= 3)) {
      setFilters(prev => ({
        ...prev,
        search: searchInput
      }));
      setPagination(prev => ({ ...prev, current_page: 1 }));
    }
  }, [filters.search, searchInput]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'search') {
      setSearchInput(value);
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
      setPagination(prev => ({ ...prev, current_page: 1 }));
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      status: '',
      from_date: '',
      to_date: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const formatStatus = (statusValue) => {
    return getStatusConfig(statusValue).label;
  };

  const getStatusClass = (statusValue) => {
    return statusValue ? `request-status-${statusValue}` : 'request-status-default';
  };

  const handleViewDetail = (requestId, customerId) => {
    setSelectedRequest({ id: requestId, customer: { id: customerId } });
    setShowDetailModal(true);
  };

  const handleGoToCustomer = (dni) => {
    navigate(`/clientes/${dni}`);
  };

  // Componente Skeleton para la tabla
  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.per_page }).map((_, index) => (
        <tr key={index} className="requests-page__row-skeleton">
          <td data-label="Solicitud"><Skeleton width={56} height={16} /></td>
          <td data-label="Cliente">
            <div className="requests-page__cell-stack">
              <Skeleton width={144} height={16} />
              <Skeleton width={88} height={12} />
            </div>
          </td>
          <td data-label="Fecha"><Skeleton width={88} height={14} /></td>
          <td data-label="Vendedor"><Skeleton width={112} height={14} /></td>
          <td data-label="Monto"><Skeleton width={96} height={16} /></td>
          <td data-label="Cuotas"><Skeleton width={34} height={16} /></td>
          <td data-label="Estado" className="text-center">
            <Skeleton width={104} height={28} borderRadius={999} />
          </td>
          <td data-label="Acciones">
            <div className="requests-page__actions">
              <Skeleton width={64} height={34} borderRadius={10} />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  // Componente de Paginación
  const Pagination = ({ loading }) => (
    <nav aria-label="Navegación de páginas" className="requests-page__pagination-nav">
      <ul className="pagination justify-content-center mb-0">
        <li className={`page-item ${loading || pagination.current_page === 1 ? 'disabled' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={loading}
          >
            Anterior
          </button>
        </li>
        {Array.from({ length: pagination.last_page }, (_, i) => (
          <li key={i} className={`page-item ${pagination.current_page === i + 1 ? 'active' : ''} ${loading ? 'disabled' : ''}`}>
            <button 
              className="page-link"
              onClick={() => setPagination(prev => ({ ...prev, current_page: i + 1 }))}
              disabled={loading}
            >
              {i + 1}
            </button>
          </li>
        ))}
        <li className={`page-item ${loading || pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
          <button 
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={loading}
          >
            Siguiente
          </button>
        </li>
      </ul>
    </nav>
  );

  return (
    <div className="page-content requests-page rg-shell-content">
      <header className="requests-page__heading">
        <div>
          <h1 className="requests-page__title">{activePreset?.label || 'Créditos'}</h1>
          <p className="requests-page__lead">
            {activePreset?.lead || 'Consultá operaciones generadas, revisá su estado y abrí el detalle del cliente sin salir del módulo.'}
          </p>
        </div>
        <div className="requests-page__heading-actions">
          <BackHomeButton />
        </div>
      </header>

      <section className="section requests-page__section">
        <TableCard
          className="requests-page__table-card"
          bodyClassName="requests-page__table-card-body"
          toolbarClassName="requests-page__toolbar"
          tableClassName="requests-page__table-wrap"
          paginationClassName="requests-page__pagination"
          header={(
            <div className="requests-page__context-bar">
              <div className="requests-page__context-copy">
                <span className="requests-page__context-eyebrow">{activePreset?.eyebrow || 'Seguimiento'}</span>
                <p className="requests-page__context-text">
                  {loading
                    ? 'Actualizando operaciones...'
                    : `${pagination.total || 0} operación${pagination.total === 1 ? '' : 'es'} registradas`}
                </p>
              </div>
              <div className="requests-page__context-actions">
                <span className="requests-page__context-pill">
                  {hasActiveFilters ? filterSummary : 'Sin filtros activos'}
                </span>
              </div>
            </div>
          )}
          toolbar={
            <div className="requests-page__filters">
              <div className="row g-3">
                <div className="col-12 col-lg-4">
                  <FormControl
                    id="requests-search"
                    name="search"
                    label="Búsqueda general"
                    placeholder="Cliente o DNI"
                    value={searchInput}
                    onChange={handleFilterChange}
                    hint="La búsqueda se aplica cuando está vacía o con 3 caracteres en adelante."
                    iconLeft={<i className="bi bi-search" aria-hidden />}
                  />
                </div>
                <div className="col-12 col-sm-6 col-lg-3">
                  <FormControl
                    id="requests-status"
                    as="select"
                    name="status"
                    label="Estado"
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">Todos los estados</option>
                    {Object.entries(REQUEST_STATUS_CONFIG).map(([value, { label }]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </FormControl>
                </div>
                <div className="col-12 col-sm-6 col-lg-2">
                  <FormControl
                    id="requests-from-date"
                    type="date"
                    name="from_date"
                    label="Desde"
                    value={filters.from_date}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-12 col-sm-6 col-lg-2">
                  <FormControl
                    id="requests-to-date"
                    type="date"
                    name="to_date"
                    label="Hasta"
                    value={filters.to_date}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-12 col-lg-1 requests-page__filters-actions-col">
                  <div className="requests-page__filters-actions">
                    <Button
                      variant="ghost"
                      className="requests-page__filter-button"
                      onClick={clearFilters}
                      disabled={!hasActiveFilters}
                    >
                      <i className="bi bi-eraser" aria-hidden />
                      Limpiar
                    </Button>
                    <Button
                      variant="outlineSecondary"
                      className="requests-page__filter-button"
                      onClick={fetchRequests}
                      disabled={loading}
                    >
                      <i className={`bi bi-arrow-clockwise${loading ? ' rg-spin' : ''}`} aria-hidden />
                      Actualizar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          }
          table={
            <table className="table table-hover requests-page__table">
              <thead>
                <tr>
                  <th>Solicitud</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Vendedor</th>
                  <th>Monto</th>
                  <th>Cuotas</th>
                  <th className="text-center">Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton />
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="requests-page__empty-cell">
                      <i className="bi bi-inbox requests-page__empty-icon" aria-hidden />
                      <div className="requests-page__empty-title">No hay operaciones para mostrar</div>
                      <p className="requests-page__empty-copy">
                        {hasActiveFilters
                          ? 'Probá ajustar los filtros para volver a ver resultados.'
                          : 'Las nuevas operaciones aparecerán aquí a medida que se registren.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  requests.map((request) => (
                    <tr key={request.id}>
                      <td data-label="Solicitud">#{request.id}</td>
                      <td data-label="Cliente">
                        <button
                          type="button"
                          onClick={() => {
                            handleGoToCustomer(request.customer.dni);
                          }}
                          className="rg-link-button requests-page__customer-link"
                        >
                          {request.customer.name}
                        </button>
                        <br />
                        <small className="requests-page__customer-meta">
                          DNI: {request.customer.dni}
                        </small>
                      </td>
                      <td data-label="Fecha">{formatDate(request.request_date)}</td>
                      <td data-label="Vendedor">{request.seller.full_name}</td>
                      <td data-label="Monto">{formatCurrency(request.amount)}</td>
                      <td data-label="Cuotas" className="text-center">{request.installments}</td>
                      <td data-label="Estado" className="text-center">
                        <span
                          className={`request-status-badge ${getStatusClass(request.status?.value)}`}
                        >
                          {request.status?.label || formatStatus(request.status?.value)}
                        </span>
                      </td>
                      <td data-label="Acciones">
                        <div className="btn-group requests-page__actions">
                          <button
                            className="btn btn-sm btn-outline-primary requests-page__view-btn"
                            onClick={() => handleViewDetail(request.id, request.customer.id)}
                            title="Ver detalle"
                          >
                            <i className="bi bi-eye me-1"></i>
                            Ver
                          </button>
                          {/* <button 
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleUploadDocuments(request.id)}
                            title="Documentos"
                          >
                            <i className="bi bi-file-earmark-text"></i>
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          }
          pagination={<Pagination loading={loading} />}
        />

        {showDetailModal && (
          <RequestDetailModal
            requestId={selectedRequest.id}
            customerId={selectedRequest.customer.id}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedRequest(null);
            }}
          />
        )}
      </section>
    </div>
  );
};

export default RequestsPage; 